import React, { FunctionComponent, PropsWithChildren, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { Icon } from '../icons/icon/icon-component';
import styles from './accordion-component.module.scss';

export type AccordionProps = PropsWithChildren<{
  title: ReactNode;
  subtitle?: ReactNode;
  disabled?: boolean;
  className?: string;
  headingWrapperClassname?: string;
  headingClassName?: string;
  headerId?: string;
  isHeaderFixed?: boolean;
  isOpenByDefault?: boolean;
  disableToggle?: boolean;
  onAccordionToggle?: (open: boolean) => void;
}>;

// TODO: refactor
// https://dev.to/producthackers/intersection-observer-using-react-49ko
// https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
const useElementOnScreen = (options: IntersectionObserverInit): [RefObject<HTMLDivElement>, boolean] => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHidden, setIsHidden] = useState(false);

  const callbackFunction = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsHidden(!entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFunction, options);
    const container = containerRef.current;
    if (container) observer.observe(container);

    return () => {
      if (container) observer.unobserve(container);
    };
  }, [containerRef, options]);

  return [containerRef, isHidden];
};

export const Accordion: FunctionComponent<AccordionProps> = (props: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(props.isOpenByDefault ?? false);
  const [productsPanelOpenHeight, setProductsPanelOpenHeight] = useState(props.isOpenByDefault ? 'none' : undefined);

  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const accordionRef = useRef<HTMLDivElement>(null);

  const [containerRef, isOutOfViewPort] = useElementOnScreen({
    root: null,
    rootMargin: '0px',
    threshold: 1.0,
  });

  useEffect(() => {
    if (!panelRef.current) return;

    if (!isOpen) {
      panelRef.current.style.overflow = 'hidden';
      panelRef.current.style.maxHeight = panelRef.current.scrollHeight + 'px'; // ensure transition starts from current height

      // trigger transition, requestAnimationFrame ensures that the browser has applied the initial style before changing maxHeight, so the transition runs correctly.
      requestAnimationFrame(() => {
        if (panelRef.current) panelRef.current.style.maxHeight = '0';
      });

      if (visibilityTimeoutRef.current) clearTimeout(visibilityTimeoutRef.current);
      visibilityTimeoutRef.current = setTimeout(() => {
        if (panelRef.current) panelRef.current.style.display = 'none';
      }, 300);
    } else {
      panelRef.current.style.display = 'block';
      panelRef.current.style.maxHeight = '0'; // start from 0
      panelRef.current.style.overflow = 'hidden';

      // trigger opening transition
      requestAnimationFrame(() => {
        if (panelRef.current) panelRef.current.style.maxHeight = productsPanelOpenHeight ?? panelRef.current.scrollHeight + 'px';
      });

      // Clear previous timeout (if useEffect is called multiple times)
      if (visibilityTimeoutRef.current) clearTimeout(visibilityTimeoutRef.current);
      visibilityTimeoutRef.current = setTimeout(() => {
        if (panelRef.current) panelRef.current.style.overflow = 'visible';
      }, 300);
    }

    if (!props.disabled && !props.disableToggle && props.onAccordionToggle) {
      props.onAccordionToggle(isOpen);
    }

    return () => {
      if (visibilityTimeoutRef.current) clearTimeout(visibilityTimeoutRef.current);
    };
  }, [isOpen, productsPanelOpenHeight, props]);

  useEffect(() => {
    function onResize(entries: ResizeObserverEntry[]) {
      if (!isOpen || !panelRef.current) return;

      for (const entry of entries) {
        panelRef.current.style.maxHeight = entry.target.scrollHeight + 'px';
      }
    }

    if (childrenRef.current) {
      const observer = new ResizeObserver(onResize);
      observer.observe(childrenRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [childrenRef, isOpen]);

  //reset to undefined so that maxHeight is set to actual panel height & not 'none' in the above React hook
  useEffect(() => {
    if (productsPanelOpenHeight === 'none') {
      visibilityTimeoutRef.current = setTimeout(() => {
        setProductsPanelOpenHeight(undefined);
      }, 300);

      return () => {
        if (visibilityTimeoutRef.current) clearTimeout(visibilityTimeoutRef.current);
      };
    }
  }, [productsPanelOpenHeight]);

  //re-checked whenever props are updated
  if (props.disabled && isOpen) {
    setIsOpen(false);
  }

  return (
    <div
      className={`${styles.accordion} ${isOpen ? styles.open : ''} ${props.disabled ? styles.disabled : ''} ${
        props.disableToggle ? styles['disable-toggle'] : ''
      } ${props.className ?? ''}`}
      ref={accordionRef}
    >
      <div
        data-testid='accordion'
        id={props.headerId}
        ref={headerRef}
        className={`${styles.header} ${props.isHeaderFixed && isOpen ? styles.fixed : ''} ${isOutOfViewPort ? styles.scrolled : ''} ${
          props.headingWrapperClassname ?? ''
        }`}
        onClick={() => {
          if (!props.disabled && !props.disableToggle) setIsOpen(!isOpen);
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.keyCode === 32) /** Spacebar */ && !props.disabled && !props.disableToggle) {
            setIsOpen(!isOpen);
          }
        }}
        tabIndex={0}
      >
        <div className={`${styles.heading} ${props.headingClassName ?? ''}`}>
          <div className={styles.title}>{props.title}</div>
          {props.subtitle && <div className={props.subtitle ? styles.subtitle : ''}>{props.subtitle}</div>}
        </div>
        {!props.disabled && !props.disableToggle && <Icon height={24} name='chev-down' className={styles.chevron}></Icon>}
      </div>
      <div className={styles.panel} ref={panelRef} data-testid='accordion-panel'>
        <div ref={containerRef}>{/* needed to check if out of viewport */}</div>
        <div ref={childrenRef}>{props.children}</div>
      </div>
    </div>
  );
};

export default Accordion;
